// Centralized debug utility for controlled console output
export const DEBUG = {
  // Environment-based debug flags
  isEnabled: import.meta.env.DEV && import.meta.env.VITE_DEBUG === 'true',
  isVerbose: import.meta.env.DEV && import.meta.env.VITE_DEBUG_VERBOSE === 'true',
  
  // Debug categories for granular control
  categories: {
    api: import.meta.env.DEV && (import.meta.env.VITE_DEBUG_API === 'true' || import.meta.env.VITE_DEBUG === 'true'),
    auth: import.meta.env.DEV && (import.meta.env.VITE_DEBUG_AUTH === 'true' || import.meta.env.VITE_DEBUG === 'true'),
    locations: import.meta.env.DEV && (import.meta.env.VITE_DEBUG_LOCATIONS === 'true' || import.meta.env.VITE_DEBUG === 'true'),
    dips: import.meta.env.DEV && (import.meta.env.VITE_DEBUG_DIPS === 'true' || import.meta.env.VITE_DEBUG === 'true'),
    favourites: import.meta.env.DEV && (import.meta.env.VITE_DEBUG_FAVOURITES === 'true' || import.meta.env.VITE_DEBUG === 'true'),
    ui: import.meta.env.DEV && (import.meta.env.VITE_DEBUG_UI === 'true' || import.meta.env.VITE_DEBUG === 'true'),
    performance: import.meta.env.DEV && (import.meta.env.VITE_DEBUG_PERFORMANCE === 'true' || import.meta.env.VITE_DEBUG === 'true'),
  }
};

// Debug logger with category support
export const debugLog = {
  // General debug logging
  log: (message: string, data?: any) => {
    if (DEBUG.isEnabled) {
      console.log(`[DEBUG] ${message}`, data || '');
    }
  },

  // Category-specific logging
  api: (message: string, data?: any) => {
    if (DEBUG.categories.api) {
      console.log(`[DEBUG-API] ${message}`, data || '');
    }
  },

  auth: (message: string, data?: any) => {
    if (DEBUG.categories.auth) {
      console.log(`[DEBUG-AUTH] ${message}`, data || '');
    }
  },

  locations: (message: string, data?: any) => {
    if (DEBUG.categories.locations) {
      console.log(`[DEBUG-LOCATIONS] ${message}`, data || '');
    }
  },

  dips: (message: string, data?: any) => {
    if (DEBUG.categories.dips) {
      console.log(`[DEBUG-DIPS] ${message}`, data || '');
    }
  },

  favourites: (message: string, data?: any) => {
    if (DEBUG.categories.favourites) {
      console.log(`[DEBUG-FAVOURITES] ${message}`, data || '');
    }
  },

  ui: (message: string, data?: any) => {
    if (DEBUG.categories.ui) {
      console.log(`[DEBUG-UI] ${message}`, data || '');
    }
  },

  performance: (message: string, data?: any) => {
    if (DEBUG.categories.performance) {
      console.log(`[DEBUG-PERFORMANCE] ${message}`, data || '');
    }
  },

  // Error logging (always enabled in dev)
  error: (message: string, error?: any) => {
    if (import.meta.env.DEV) {
      console.error(`[DEBUG-ERROR] ${message}`, error || '');
    }
  },

  // Warning logging (always enabled in dev)
  warn: (message: string, data?: any) => {
    if (import.meta.env.DEV) {
      console.warn(`[DEBUG-WARN] ${message}`, data || '');
    }
  },

  // Verbose logging (only when VITE_DEBUG_VERBOSE is true)
  verbose: (message: string, data?: any) => {
    if (DEBUG.isVerbose) {
      console.log(`[DEBUG-VERBOSE] ${message}`, data || '');
    }
  }
};

// Helper function to check if debug is enabled
export const isDebugEnabled = () => DEBUG.isEnabled;

// Helper function to check if a specific category is enabled
export const isDebugCategoryEnabled = (category: keyof typeof DEBUG.categories) => {
  return DEBUG.categories[category];
};

// Performance timing helper
export const debugTime = {
  start: (label: string) => {
    if (DEBUG.categories.performance) {
      console.time(`[DEBUG-PERFORMANCE] ${label}`);
    }
  },
  
  end: (label: string) => {
    if (DEBUG.categories.performance) {
      console.timeEnd(`[DEBUG-PERFORMANCE] ${label}`);
    }
  }
};

// API request/response logging helper
export const debugApi = {
  request: (method: string, url: string, data?: any) => {
    debugLog.api(`API Request: ${method} ${url}`, data);
  },
  
  response: (method: string, url: string, status: number, data?: any) => {
    debugLog.api(`API Response: ${method} ${url} (${status})`, data);
  },
  
  error: (method: string, url: string, error: any) => {
    debugLog.error(`API Error: ${method} ${url}`, error);
  }
}; 